/**
 * Shared model + helpers for the multi-stage Stages tab.
 *
 * Source-of-truth split (mirrors the backend contract):
 *   - `Event.stages_json` is SPLIT: start_time/date/timeout_seconds
 *     are BEHAVIORAL — the backend resolves them into the geofence_set
 *     payload's per-stage schedule (start_utc + max_duration_s), and
 *     saving a change to them bumps the geofence revision so phones
 *     re-sync. name/type/car_interval/laps remain cosmetic (no bump,
 *     phones never see them).
 *   - Gates keyed by `stage_index` are the positional truth. The
 *     evaluator has no per-stage GATE tracking yet, so ONLY
 *     stage_index==0 gates are emitted to phones — UI copy must stay
 *     honest about that.
 *
 * The stage list is the MERGE of both: every index that appears in
 * stages_json or carries gates gets a row. Index 0 always exists (the
 * single-stage default: name = event name, type = event type) so an
 * event with no stages_json behaves exactly like the pre-multi-stage
 * StagesTab.
 */

import { GatePos } from '@/components/GateMapPicker';
import {
  EventGate,
  EventRow,
  FleetApiError,
  StageMeta,
  StagesJson,
} from '@/services/fleet';

// ── Constants (shared with GatePane; values match the old StagesTab) ──

export const DEFAULT_RADIUS_M = 50;
export const RADIUS_MIN = 5;
export const RADIUS_MAX = 500;
export const TIMEOUT_MIN_MINUTES = 1;
export const TIMEOUT_MAX_MINUTES = 1440;
export const DEFAULT_TIMEOUT_MINUTES = 25;
export const MAX_STAGE_INDEX = 63; // backend Field(ge=0, le=63)
export const DEFAULT_LAPS = 10;
export const DEFAULT_CAR_INTERVAL_SECONDS = 60;

/** 'HH:MM' 24h — what StageMeta.start_time stores. */
export const TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

/**
 * Canonicalize an HH:MM field for sending: trim + zero-pad a
 * single-digit hour ('9:31' → '09:31'). TIME_RE deliberately accepts
 * the single-digit form (friendlier to type), but the backend patterns
 * (`^([01]\d|2[0-3]):[0-5]\d$`) require two digits — every send path
 * must pass through this or the API 422s on '9:31'. Values that don't
 * look like a time pass through trimmed; callers validate separately.
 */
export function normalizeHHMM(value: string): string {
  const t = value.trim();
  return /^\d:/.test(t) ? `0${t}` : t;
}

/** 'YYYY-MM-DD' — what StageMeta.date stores (optional per-stage date). */
export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const CAR_INTERVAL_OPTIONS: ReadonlyArray<{ seconds: number; label: string }> = [
  { seconds: 30, label: '0:30' },
  { seconds: 60, label: '1:00' },
  { seconds: 120, label: '2:00' },
];

export type StageType = 'Stage' | 'Circuit';
export type StageStatus = 'draft' | 'scheduled' | 'live' | 'complete';

// ── Merged stage view ─────────────────────────────────────────────────

export interface StageView {
  index: number;
  /** '' when the stage has gates but no metadata (renders "Unnamed stage"). */
  name: string;
  type: StageType;
  /** 'HH:MM' event-local, or null when unscheduled. */
  startTime: string | null;
  /** 'YYYY-MM-DD' event-local, or null when no per-stage date set. */
  date: string | null;
  carIntervalSeconds: number | null;
  /** Stage 0 always reflects event.max_stage_seconds (the behavioral
   *  field phones actually receive); other stages read stages_json. */
  timeoutSeconds: number;
  laps: number | null;
  reverseOf: number | null;
  hasStart: boolean;
  hasFinish: boolean;
  /** True when the stage carries a corridor route. */
  hasRoute: boolean;
  /** True when a stages_json entry exists for this index. */
  hasMeta: boolean;
}

export function normalizeStageType(t: string | null | undefined): StageType {
  return (t ?? '').trim().toLowerCase() === 'circuit' ? 'Circuit' : 'Stage';
}

/** Display label: SS1/SS2… for rally stages, H1/H2… for circuit heats. */
export function stageLabel(index: number, type: StageType): string {
  return `${type === 'Circuit' ? 'H' : 'SS'}${index + 1}`;
}

export function findStageGate(
  gates: EventGate[],
  kind: 'start' | 'finish',
  stageIndex: number,
): EventGate | undefined {
  return gates.find((g) => g.kind === kind && g.stage_index === stageIndex);
}

/** All gates belonging to a stage (start + finish + any lap_line). */
export function stageGates(gates: EventGate[], stageIndex: number): EventGate[] {
  return gates.filter((g) => g.stage_index === stageIndex);
}

function metaByIndex(e: EventRow): Map<number, StageMeta> {
  const m = new Map<number, StageMeta>();
  for (const s of e.stages_json?.stages ?? []) m.set(s.index, s);
  return m;
}

function buildStageView(e: EventRow, index: number, meta: StageMeta | undefined): StageView {
  const isZero = index === 0;
  // Stage-0 defaults keep the pre-multi-stage behavior: name = event
  // name, timeout = event.max_stage_seconds.
  // Type derives from event.type for EVERY stage, never from meta: the
  // editor disables the per-stage toggle above index 0 ("Event type
  // governs — see SS1"), so a meta-sourced type was unreachable to fix —
  // stage 2+ of a circuit event silently stayed a rally Stage, losing
  // Laps/heat scheduling. Ignoring stale meta.type also self-heals the
  // events already saved with the wrong value.
  const type = normalizeStageType(e.type);
  return {
    index,
    name: meta?.name ?? (isZero ? e.name : ''),
    type,
    startTime: meta?.start_time ?? null,
    date: meta?.date ?? null,
    carIntervalSeconds: meta?.car_interval_seconds ?? null,
    // Stage 0's safety timeout is event.max_stage_seconds, period —
    // that's the field the phone evaluator actually enforces. Other
    // stages keep a cosmetic per-stage value in stages_json.
    timeoutSeconds: isZero
      ? e.max_stage_seconds
      : meta?.timeout_seconds ?? DEFAULT_TIMEOUT_MINUTES * 60,
    laps: meta?.laps ?? null,
    reverseOf: meta?.reverse_of ?? null,
    hasStart: findStageGate(e.gates, 'start', index) != null,
    hasFinish: findStageGate(e.gates, 'finish', index) != null,
    hasRoute: (e.stage_route_indices ?? []).includes(index),
    hasMeta: meta != null,
  };
}

/**
 * Stage list = stages_json metadata MERGED with the gates' actual
 * stage_index values. A stage_index that has gates but no metadata
 * still shows (unnamed). Index 0 always exists.
 */
export function buildStageViews(e: EventRow): StageView[] {
  const metas = metaByIndex(e);
  const indices = new Set<number>([0]);
  for (const idx of metas.keys()) indices.add(idx);
  for (const g of e.gates) indices.add(g.stage_index);
  return [...indices]
    .sort((a, b) => a - b)
    .map((idx) => buildStageView(e, idx, metas.get(idx)));
}

// ── Status (client-side MVP approximation) ────────────────────────────

/**
 * A stage is operational once it has ANY start trigger — the evaluator
 * can start recording from a start gate, a scheduled start_time, or
 * corridor entry (route uploaded). A stop authority always exists (the
 * safety timeout), so finish gates are never required for readiness;
 * they just make the stop precise.
 */
export function hasStartTrigger(view: StageView): boolean {
  return (
    view.hasStart ||
    view.hasRoute ||
    (view.startTime != null && TIME_RE.test(view.startTime))
  );
}

/**
 * Status chips are an MVP approximation computed client-side:
 *   - Draft:     no start trigger yet (no start gate, no start_time,
 *                no corridor route) — phones can never begin this stage.
 *   - Scheduled: now is before the event window opens, or (within the
 *                window) before today's stage start_time.
 *   - Live:      within the event window, past the stage start (or no
 *                start_time set).
 *   - Complete:  the event window has closed.
 * Known approximations, accepted for MVP: start_time is event-local
 * 'HH:MM' with no date, so it is compared against the BROWSER's local
 * time-of-day "today" — correct when operator and event share a
 * timezone, off by the tz delta otherwise. There is no per-stage
 * completion signal yet (the evaluator only tracks stage 0), so a
 * mid-window stage past its start time reads Live even if every car
 * already finished it.
 */
export function computeStageStatus(view: StageView, e: EventRow, now: Date): StageStatus {
  if (!hasStartTrigger(view)) return 'draft';
  const t = now.getTime();
  const from = Date.parse(e.active_from);
  // Open-ended events (active_until null) never read Complete.
  const until = e.active_until ? Date.parse(e.active_until) : NaN;
  if (Number.isFinite(until) && t > until) return 'complete';
  if (Number.isFinite(from) && t < from) return 'scheduled';
  if (view.startTime && TIME_RE.test(view.startTime)) {
    const [hh, mm] = view.startTime.split(':').map(Number);
    const stageToday = new Date(now);
    stageToday.setHours(hh, mm, 0, 0);
    if (t < stageToday.getTime()) return 'scheduled';
  }
  return 'live';
}

export const STAGE_STATUS_LABEL: Record<StageStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  live: 'Live',
  complete: 'Complete',
};

// ── Editor form state ─────────────────────────────────────────────────

export interface StageFormState {
  name: string;
  type: StageType;
  /** Raw 'HH:MM' text; '' = unset. Validated against TIME_RE on save. */
  startTime: string;
  /** Raw 'YYYY-MM-DD' text; '' = unset. Validated against DATE_RE on save. */
  date: string;
  carIntervalSeconds: number;
  timeoutMinutes: number;
  laps: number;
  start: GatePos;
  finish: GatePos;
}

export function stageToForm(e: EventRow, index: number): StageFormState {
  const view = buildStageView(e, index, metaByIndex(e).get(index));
  const s = findStageGate(e.gates, 'start', index);
  const f = findStageGate(e.gates, 'finish', index);
  return {
    name: view.name,
    type: view.type,
    startTime: view.startTime ?? '',
    date: view.date ?? '',
    carIntervalSeconds: view.carIntervalSeconds ?? DEFAULT_CAR_INTERVAL_SECONDS,
    timeoutMinutes: Math.max(1, Math.round(view.timeoutSeconds / 60)),
    laps: view.laps ?? DEFAULT_LAPS,
    start: {
      lat: s?.lat ?? null,
      lon: s?.lon ?? null,
      radius_m: s?.radius_m ?? DEFAULT_RADIUS_M,
    },
    finish: {
      lat: f?.lat ?? null,
      lon: f?.lon ?? null,
      radius_m: f?.radius_m ?? DEFAULT_RADIUS_M,
    },
  };
}

/**
 * Canonical StageMeta for a form snapshot. Running both the original
 * and the edited form through this makes "did the metadata change?" a
 * plain JSON comparison.
 *
 * Stage 0 stores timeout_seconds as null: its safety timeout lives on
 * event.max_stage_seconds (behavioral, phone-enforced) and mirroring
 * it into cosmetic metadata would just invite drift.
 */
export function metaFromForm(
  index: number,
  form: StageFormState,
  existing: StageMeta | undefined,
): StageMeta {
  return {
    index,
    name: form.name.slice(0, 64),
    // Type is event.type for every stage, edited on the event itself (see
    // StageEditorPane) — persisting it here too would just grow another
    // stale copy for buildStageView to have to ignore.
    type: null,
    start_time: form.startTime.trim() === '' ? null : normalizeHHMM(form.startTime),
    date: form.date.trim() === '' ? null : form.date.trim(),
    car_interval_seconds: form.type === 'Stage' ? form.carIntervalSeconds : null,
    timeout_seconds: index === 0 ? null : form.timeoutMinutes * 60,
    laps: form.type === 'Circuit' ? form.laps : null,
    // No UI to set reverse_of in MVP — preserve whatever is stored.
    reverse_of: existing?.reverse_of ?? null,
  };
}

export function findStageMeta(e: EventRow, index: number): StageMeta | undefined {
  return metaByIndex(e).get(index);
}

/** stages_json with `meta` inserted/replaced at its index, sorted. */
export function upsertStageMeta(existing: StagesJson | null | undefined, meta: StageMeta): StagesJson {
  const stages = (existing?.stages ?? []).filter((s) => s.index !== meta.index);
  stages.push(meta);
  stages.sort((a, b) => a.index - b.index);
  return { version: existing?.version ?? 1, stages };
}

/** stages_json with the given index's metadata removed. */
export function removeStageMeta(existing: StagesJson | null | undefined, index: number): StagesJson {
  return {
    version: existing?.version ?? 1,
    stages: (existing?.stages ?? []).filter((s) => s.index !== index),
  };
}

/** Smallest unused index 0–63, or null when all 64 slots are taken. */
export function nextFreeStageIndex(views: StageView[]): number | null {
  const used = new Set(views.map((v) => v.index));
  for (let i = 0; i <= MAX_STAGE_INDEX; i++) {
    if (!used.has(i)) return i;
  }
  return null;
}

// ── Misc utils (moved verbatim from the old StagesTab) ────────────────

export function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function formatError(err: unknown): string {
  if (err instanceof FleetApiError) {
    if (typeof err.detail === 'string') return err.detail;
    if (Array.isArray(err.detail)) {
      return err.detail
        .map((e: any) => `${(e.loc || []).join('.')}: ${e.msg}`)
        .join('; ');
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'request failed';
}
